import React, { useState, useEffect } from 'react';
import {
  Alert,
  Snackbar,
  IconButton,
  Typography,
  Box,
  Chip,
  Collapse,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  ContactSupport as ContactSupportIcon
} from '@mui/icons-material';

export interface ErrorNotificationData {
  id: string;
  message: string;
  details?: string;
  timestamp: Date;
  url?: string;
  method?: string;
  status?: number;
}

interface ErrorNotificationProps {
  error: ErrorNotificationData | null;
  onClose: () => void;
}

/**
 * Komponent czerwonej plakietki z informacją o błędzie aplikacji
 * Wyświetla błędy HTTP zamiast ponawiania requestów
 */
export const ErrorNotification: React.FC<ErrorNotificationProps> = ({ error, onClose }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!error) return null;

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleContactSupport = () => {
    // Można dodać link do systemu ticketów lub email
    const subject = encodeURIComponent(`Błąd aplikacji: ${error.message}`);
    const body = encodeURIComponent(`
Opis błędu: ${error.message}
Czas wystąpienia: ${error.timestamp.toLocaleString()}
URL: ${error.url || 'Nieznany'}
Metoda HTTP: ${error.method || 'Nieznana'}
Status HTTP: ${error.status || 'Nieznany'}
Szczegóły: ${error.details || 'Brak dodatkowych szczegółów'}

Proszę opisać co robiłeś/aś gdy wystąpił błąd:
[Miejsce na opis użytkownika]
    `);
    
    window.open(`mailto:support@example.com?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <Snackbar
      open={true}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ 
        zIndex: 9999,
        '& .MuiSnackbarContent-root': {
          padding: 0
        }
      }}
    >
      <Alert
        severity="error"
        variant="filled"
        sx={{
          minWidth: '400px',
          maxWidth: '600px',
          backgroundColor: '#d32f2f',
          color: 'white',
          '& .MuiAlert-icon': {
            color: 'white'
          }
        }}
        icon={<ErrorIcon />}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Błąd aplikacji
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error.message}
          </Typography>

          {error.status && (
            <Chip
              label={`HTTP ${error.status}`}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                mr: 1,
                mb: 1
              }}
            />
          )}

          <Chip
            label={error.timestamp.toLocaleTimeString()}
            size="small"
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.2)', 
              color: 'white',
              mb: 1
            }}
          />

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.8)',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Odśwież stronę
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<ContactSupportIcon />}
              onClick={handleContactSupport}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.8)',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Kontakt z pomocą
            </Button>

            {(error.details || error.url) && (
              <Button
                size="small"
                variant="text"
                startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowDetails(!showDetails)}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                {showDetails ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
              </Button>
            )}
          </Box>

          <Collapse in={showDetails}>
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                {error.url && (
                  <div><strong>URL:</strong> {error.url}</div>
                )}
                {error.method && (
                  <div><strong>Metoda:</strong> {error.method}</div>
                )}
                {error.details && (
                  <div><strong>Szczegóły:</strong> {error.details}</div>
                )}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Alert>
    </Snackbar>
  );
};

/**
 * Hook do zarządzania powiadomieniami o błędach
 */
export const useErrorNotification = () => {
  const [currentError, setCurrentError] = useState<ErrorNotificationData | null>(null);

  const showError = (error: Omit<ErrorNotificationData, 'id' | 'timestamp'>) => {
    const errorData: ErrorNotificationData = {
      ...error,
      id: Date.now().toString(),
      timestamp: new Date()
    };
    setCurrentError(errorData);
  };

  const hideError = () => {
    setCurrentError(null);
  };

  return {
    currentError,
    showError,
    hideError
  };
};

export default ErrorNotification;